import { QueryClient } from '@tanstack/react-query';

/**
 * The app's single in-memory data cache (TanStack Query).
 *
 * ## What it is for
 * Every screen used to refetch everything it needed on mount, so switching tabs or toggling back to a
 * date you had just looked at re-downloaded the same rows and flashed a loading state. The cache keeps
 * what has been fetched — reference data (employees, hospitals, rooms, credentials, users) and per-day
 * data (shifts, cases, allocations keyed by local date) — so a revisit renders immediately and then
 * revalidates in the background.
 *
 * ## Rules that keep it safe
 * - **In memory only.** There is deliberately NO persister. The cache holds case schedules and Admin-only
 *   fields such as `Employee.isPregnant`; none of it may be written to localStorage or IndexedDB.
 * - **Cleared on sign-out** (`AuthContext.logout`) and whenever the signed-in identity changes
 *   (`CacheIdentityGuard`), so one user's or tenant's data never renders for another.
 * - **Mutations invalidate centrally** in `services/api.ts` via `data/invalidation.ts`, so a write can't
 *   leave a stale screen behind because a call site forgot to refresh.
 */

/** How long reference data (people, sites, rooms, credentials, users) is served without revalidating. */
export const REFERENCE_DATA_STALE_MS = 5 * 60 * 1000;
/** How long per-day operational data (shifts, cases, allocations) is served without revalidating. */
export const DAY_DATA_STALE_MS = 30 * 1000;
/** How long an entry nobody is looking at stays in memory before it is dropped. */
export const UNUSED_ENTRY_GC_MS = 30 * 60 * 1000;

export const createAppQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DAY_DATA_STALE_MS,
        gcTime: UNUSED_ENTRY_GC_MS,
        retry: 1,
        refetchOnWindowFocus: true,
      },
    },
  });

/** The app-wide instance. `services/api.ts` invalidates against it, so it must be a singleton. */
export const queryClient = createAppQueryClient();

/** Drop every cached entry (sign-out, identity change). Safe to call when the cache is empty. */
export const clearDataCache = (): void => {
  queryClient.cancelQueries();
  queryClient.clear();
};
